import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, PageWrapper } from 'components/partials';
import { Button, Checkbox, Label, RadioButtonsGroup, Spinner, TextField } from '../components/widgets';
import CopyToClipboardButton from '../components/molecules/CopyToClipboardButton';
import toaster from '../utils/toaster';
import { getInviteConfig, sendInvite } from './service';
import { collectEnabledFeatureFlags, parseInviteParams, resolveOrgGroup } from './utils';
import { DEFAULT_LOGO, SUCCESS_TOAST_AUTO_CLOSE, TIER_RADIO_ITEMS, TIERS, TRANSLATION_PREFIX } from './consts';
import css from './page.css';

function AdminInvitePage({ translate }) {
    const [searchParams] = useSearchParams();
    // sfdc/isMidmarket are read live at submit; enableAttribution/company/domains seed the form (below).
    const params = useMemo(() => parseInviteParams(searchParams), [searchParams]);

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [genLink, setGenLink] = useState('');

    const [form, setForm] = useState({
        email: '',
        fname: '',
        lname: '',
        agents: '',
        logo: '',
        cname: '',
        domains: '',
        internalTestAccount: false,
        createZendeskOrg: true,
        tier: TIERS.ENTERPRISE,
        enableAttribution: false,
    });
    const [featureFlagsSelected, setFeatureFlagsSelected] = useState({});

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // The query params arrive after the Angular→React router hash sync, which can lag the first
    // render — so we can't seed the form in the useState initializer (it would capture empty
    // params). Seed the param-derived fields once, as soon as the params are available.
    const paramsSeeded = useRef(false);
    useEffect(() => {
        if (paramsSeeded.current || !params.hasSfdc) {
            return;
        }
        paramsSeeded.current = true;
        setForm(prev => ({
            ...prev,
            enableAttribution: params.enableAttribution,
            cname: params.company,
            domains: params.domains,
        }));
    }, [params]);

    useEffect(() => {
        let cancelled = false;
        getInviteConfig()
            .then(value => {
                if (!cancelled) {
                    setConfig({ orgGroups: value.org_groups, isDemoServer: value.is_demo_server });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // The selected customer type is derived from tier + attribution, mirroring `updateOrgGroup`.
    const orgGroupSelected = useMemo(
        () => resolveOrgGroup(config && config.orgGroups, { enableAttribution: form.enableAttribution, tier: form.tier }),
        [config, form.enableAttribution, form.tier]
    );
    const additionalFeatureFlags = orgGroupSelected ? orgGroupSelected.additional_permitted_feature_limit_flag : [];

    // Reset the per-flag selections whenever the resolved group changes, matching the legacy page.
    useEffect(() => {
        setFeatureFlagsSelected({});
    }, [orgGroupSelected]);

    const submit = () => {
        setSubmitting(true);
        const inviteDetails = {
            fname: form.fname,
            lname: form.lname,
            cname: form.cname,
            email: form.email.trim(),
            enableAttribution: form.enableAttribution,
            additionalFeatureFlagsToEnable: collectEnabledFeatureFlags(featureFlagsSelected),
            orgGroupSelected,
            tier: form.tier,
            internalTestAccount: form.internalTestAccount,
            logo: form.logo,
            createZendeskOrg: form.createZendeskOrg,
            domains: form.domains,
            agents: form.agents,
            sfdc: params.sfdc,
            isStandardUnified: false,
            isMidmarket: params.isMidmarket,
        };
        sendInvite(inviteDetails)
            .then(value => {
                if (value.result) {
                    setGenLink(value.link);
                    toaster.success(translate(`${TRANSLATION_PREFIX}.INVITE_SUCCESS`), { autoClose: SUCCESS_TOAST_AUTO_CLOSE });
                } else {
                    // The backend returns a human-readable error string, shown as-is (parity with the Angular alert).
                    toaster.error(value.error);
                }
            })
            .catch(() => toaster.error(translate(`${TRANSLATION_PREFIX}.INVITE_ERROR`)))
            .finally(() => setSubmitting(false));
    };

    if (loading) {
        return <Spinner show expanded />;
    }

    if (loadError) {
        return (
            <PageWrapper>
                <div className={css.message}>
                    <Translate id={`${TRANSLATION_PREFIX}.LOAD_ERROR`} />
                </div>
            </PageWrapper>
        );
    }

    if (config.isDemoServer) {
        return (
            <PageWrapper>
                <PageHeader text={`${TRANSLATION_PREFIX}.DEMO_SERVER_TITLE`} />
            </PageWrapper>
        );
    }

    if (!params.hasSfdc) {
        return (
            <PageWrapper>
                <PageHeader text={`${TRANSLATION_PREFIX}.PAGE_TITLE`} />
                <div className={css.message}>
                    <Translate id={`${TRANSLATION_PREFIX}.MISSING_SFDC`} />
                    <img
                        className={css.flowImage}
                        src="https://cdn.singular.net/static/dashboard/img/org_invite_flow.png"
                        alt=""
                    />
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className={css.wrapper}>
            <PageHeader text={`${TRANSLATION_PREFIX}.PAGE_TITLE`} />

            <form className={css.form}>
                <TextField
                    containerClass={css.textField}
                    type="text"
                    label={`${TRANSLATION_PREFIX}.EMAIL_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.EMAIL_LABEL`}
                    value={form.email}
                    onChange={value => setField('email', value)}
                />
                <TextField
                    containerClass={css.textField}
                    label={`${TRANSLATION_PREFIX}.FIRST_NAME_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.FIRST_NAME_LABEL`}
                    value={form.fname}
                    onChange={value => setField('fname', value)}
                />
                <TextField
                    containerClass={css.textField}
                    label={`${TRANSLATION_PREFIX}.LAST_NAME_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.LAST_NAME_LABEL`}
                    value={form.lname}
                    onChange={value => setField('lname', value)}
                />
                <TextField
                    containerClass={css.textField}
                    label={`${TRANSLATION_PREFIX}.DOMAINS_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.DOMAINS_LABEL`}
                    labelTooltip={`${TRANSLATION_PREFIX}.DOMAINS_TOOLTIP`}
                    value={form.domains}
                    onChange={value => setField('domains', value)}
                />
                <TextField
                    containerClass={css.textField}
                    label={`${TRANSLATION_PREFIX}.AGENTS_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.AGENTS_LABEL`}
                    labelTooltip={`${TRANSLATION_PREFIX}.AGENTS_TOOLTIP`}
                    value={form.agents}
                    onChange={value => setField('agents', value)}
                />
                <TextField
                    containerClass={css.textField}
                    label={`${TRANSLATION_PREFIX}.COMPANY_LABEL`}
                    placeholder={`${TRANSLATION_PREFIX}.COMPANY_LABEL`}
                    value={form.cname}
                    onChange={value => setField('cname', value)}
                />

                <div className={css.logoField}>
                    <TextField
                        containerClass={css.textField}
                        label={`${TRANSLATION_PREFIX}.LOGO_LABEL`}
                        placeholder={`${TRANSLATION_PREFIX}.LOGO_PLACEHOLDER`}
                        labelTooltip={`${TRANSLATION_PREFIX}.LOGO_TOOLTIP`}
                        value={form.logo}
                        onChange={value => setField('logo', value)}
                    />
                    <img className={css.logoPreview} src={form.logo || DEFAULT_LOGO} alt="" />
                </div>

                <Checkbox
                    className={css.checkbox}
                    label={<Translate id={`${TRANSLATION_PREFIX}.INTERNAL_TEST_ACCOUNT`} />}
                    checked={form.internalTestAccount}
                    onChange={checked => setField('internalTestAccount', checked)}
                />
                <Checkbox
                    className={css.checkbox}
                    label={<Translate id={`${TRANSLATION_PREFIX}.CREATE_ZENDESK_ORG`} />}
                    checked={form.createZendeskOrg}
                    onChange={checked => setField('createZendeskOrg', checked)}
                />

                <div className={css.customerTypeRow}>
                    <Translate id={`${TRANSLATION_PREFIX}.CUSTOMER_TYPE`} />
                    {orgGroupSelected && (
                        <span className={css.selectedGroup}>
                            <Translate
                                id={`${TRANSLATION_PREFIX}.SELECTED_GROUP`}
                                data={{ group: orgGroupSelected.display_name }}
                            />
                        </span>
                    )}
                </div>
                <RadioButtonsGroup
                    horizontal
                    radioItems={TIER_RADIO_ITEMS.map(item => ({ ...item, checked: form.tier === item.id }))}
                    onChange={item => setField('tier', item.id)}
                />
                <Checkbox
                    className={css.checkbox}
                    label={<Translate id={`${TRANSLATION_PREFIX}.ENABLE_ATTRIBUTION`} />}
                    checked={form.enableAttribution}
                    onChange={checked => setField('enableAttribution', checked)}
                />

                {additionalFeatureFlags.length > 0 && (
                    <div className={css.featureFlags}>
                        <Label type="h4" text={`${TRANSLATION_PREFIX}.ADDITIONAL_FEATURE_FLAGS`} />
                        {additionalFeatureFlags.map(flag => (
                            <Checkbox
                                key={flag}
                                label={flag}
                                checked={!!featureFlagsSelected[flag]}
                                onChange={checked => setFeatureFlagsSelected(prev => ({ ...prev, [flag]: checked }))}
                            />
                        ))}
                    </div>
                )}

                <Button
                    className={css.generateButton}
                    type="primary"
                    level="level1"
                    text={`${TRANSLATION_PREFIX}.GENERATE_LINK`}
                    disabled={!orgGroupSelected || submitting}
                    showSpinner={submitting}
                    onClick={submit}
                />

                {genLink && (
                    <div className={css.linkResult}>
                        <Label type="h4" text={`${TRANSLATION_PREFIX}.INVITATION_LINK`} />
                        <div className={css.linkRow}>
                            <TextField containerClass={css.textField} disabled value={genLink} onChange={() => {}} />
                            <CopyToClipboardButton textToCopy={genLink} tooltip={`${TRANSLATION_PREFIX}.COPY_LINK`} />
                        </div>
                    </div>
                )}
            </form>
        </PageWrapper>
    );
}

AdminInvitePage.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AdminInvitePage);
